
import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { FiUploadCloud, FiArrowRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import Button from '../Button';
import Select from '../Select';
import Api from '../../Api';
import './style.css';

const DB_FIELDS = [
  { value: 'name', label: 'Nome da Jornada' },
  { value: 'type', label: 'Tipo (12x36, 6x1, etc)' },
  { value: 'description', label: 'Descrição' },
  { value: 'weekly_hours', label: 'Carga Semanal' },
  { value: 'daily_hours', label: 'Carga Diária' },
  { value: 'company_ref', label: 'Empresa (ID ou Nome)' },
  { value: 'start_time', label: 'Entrada (HH:MM)' },
  { value: 'end_time', label: 'Saída (HH:MM)' },
  { value: 'break_start_time', label: 'Início Intervalo' },
  { value: 'break_end_time', label: 'Fim Intervalo' },
  { value: 'break_duration', label: 'Duração Intervalo' },
  
  // Work Days - Monday
  { value: 'monday_start', label: 'Segunda - Entrada' },
  { value: 'monday_end', label: 'Segunda - Saída' },
  { value: 'monday_break_start', label: 'Segunda - Início Intervalo' },
  { value: 'monday_break_end', label: 'Segunda - Fim Intervalo' },
  
  // Work Days - Tuesday
  { value: 'tuesday_start', label: 'Terça - Entrada' },
  { value: 'tuesday_end', label: 'Terça - Saída' },
  { value: 'tuesday_break_start', label: 'Terça - Início Intervalo' },
  { value: 'tuesday_break_end', label: 'Terça - Fim Intervalo' },

  // Work Days - Wednesday
  { value: 'wednesday_start', label: 'Quarta - Entrada' },
  { value: 'wednesday_end', label: 'Quarta - Saída' },
  { value: 'wednesday_break_start', label: 'Quarta - Início Intervalo' },
  { value: 'wednesday_break_end', label: 'Quarta - Fim Intervalo' },

  // Work Days - Thursday
  { value: 'thursday_start', label: 'Quinta - Entrada' },
  { value: 'thursday_end', label: 'Quinta - Saída' },
  { value: 'thursday_break_start', label: 'Quinta - Início Intervalo' },
  { value: 'thursday_break_end', label: 'Quinta - Fim Intervalo' },

  // Work Days - Friday
  { value: 'friday_start', label: 'Sexta - Entrada' },
  { value: 'friday_end', label: 'Sexta - Saída' },
  { value: 'friday_break_start', label: 'Sexta - Início Intervalo' },
  { value: 'friday_break_end', label: 'Sexta - Fim Intervalo' },

  // Work Days - Saturday
  { value: 'saturday_start', label: 'Sábado - Entrada' },
  { value: 'saturday_end', label: 'Sábado - Saída' },
  { value: 'saturday_break_start', label: 'Sábado - Início Intervalo' },
  { value: 'saturday_break_end', label: 'Sábado - Fim Intervalo' },

  // Work Days - Sunday
  { value: 'sunday_start', label: 'Domingo - Entrada' },
  { value: 'sunday_end', label: 'Domingo - Saída' },
  { value: 'sunday_break_start', label: 'Domingo - Início Intervalo' },
  { value: 'sunday_break_end', label: 'Domingo - Fim Intervalo' },
];

export default function JourneyImportModal({ isOpen, onClose, onSuccess, companyId, companies = [] }) {
  const [step, setStep] = useState('upload'); // upload, map
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({}); // { headerName: dbField }
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(companyId || '');

  React.useEffect(() => {
    setSelectedCompanyId(companyId || '');
  }, [companyId]);

  const normalizeString = (str) => {
    if (!str) return '';
    return String(str).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  };

  const findCompany = (companiesList, ref) => {
    if (!ref) return null;
    const normalizedRef = normalizeString(ref);
    const exactId = companiesList.find(c => String(c.id) === String(ref));
    if (exactId) return exactId;
    return companiesList.find(c => {
      const name1 = normalizeString(c.corporate_name);
      const name2 = normalizeString(c.trade_name);
      return (name1 && (name1.includes(normalizedRef) || normalizedRef.includes(name1))) ||
             (name2 && (name2.includes(normalizedRef) || normalizedRef.includes(name2)));
    }) || null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }); // Array of arrays

        if (data.length < 2) {
          toast.warning('O arquivo parece estar vazio ou sem dados.');
          return;
        }

        const fileHeaders = data[0];
        const rows = data.slice(1);

        setHeaders(fileHeaders);
        setFileData(rows);
        
        // Auto-map based on similar names
        const initialMapping = {};
        
        const normalize = (str) => String(str).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

        fileHeaders.forEach(header => {
          const normHeader = normalize(header);
          
          if (normHeader.includes('nome') || normHeader.includes('jornada')) initialMapping[header] = 'name';
          else if (normHeader.includes('empresa') || normHeader.includes('company')) initialMapping[header] = 'company_ref';
          else if (normHeader.includes('tipo')) initialMapping[header] = 'type';
          else if (normHeader.includes('descri')) initialMapping[header] = 'description';
          else if (normHeader.includes('semanal')) initialMapping[header] = 'weekly_hours';
          else if (normHeader.includes('diaria') || normHeader.includes('diario')) initialMapping[header] = 'daily_hours';
          
          // Check specific days first
          else if (normHeader.includes('segunda')) {
             if (normHeader.includes('inicio intervalo')) initialMapping[header] = 'monday_break_start';
             else if (normHeader.includes('fim intervalo')) initialMapping[header] = 'monday_break_end';
             else if (normHeader.includes('entrada') || normHeader.includes('inicio')) initialMapping[header] = 'monday_start';
             else if (normHeader.includes('saida') || normHeader.includes('fim')) initialMapping[header] = 'monday_end';
          }
          else if (normHeader.includes('terca')) {
             if (normHeader.includes('inicio intervalo')) initialMapping[header] = 'tuesday_break_start';
             else if (normHeader.includes('fim intervalo')) initialMapping[header] = 'tuesday_break_end';
             else if (normHeader.includes('entrada') || normHeader.includes('inicio')) initialMapping[header] = 'tuesday_start';
             else if (normHeader.includes('saida') || normHeader.includes('fim')) initialMapping[header] = 'tuesday_end';
          }
          else if (normHeader.includes('quarta')) {
             if (normHeader.includes('inicio intervalo')) initialMapping[header] = 'wednesday_break_start';
             else if (normHeader.includes('fim intervalo')) initialMapping[header] = 'wednesday_break_end';
             else if (normHeader.includes('entrada') || normHeader.includes('inicio')) initialMapping[header] = 'wednesday_start';
             else if (normHeader.includes('saida') || normHeader.includes('fim')) initialMapping[header] = 'wednesday_end';
          }
          else if (normHeader.includes('quinta')) {
             if (normHeader.includes('inicio intervalo')) initialMapping[header] = 'thursday_break_start';
             else if (normHeader.includes('fim intervalo')) initialMapping[header] = 'thursday_break_end';
             else if (normHeader.includes('entrada') || normHeader.includes('inicio')) initialMapping[header] = 'thursday_start';
             else if (normHeader.includes('saida') || normHeader.includes('fim')) initialMapping[header] = 'thursday_end';
          }
          else if (normHeader.includes('sexta')) {
             if (normHeader.includes('inicio intervalo')) initialMapping[header] = 'friday_break_start';
             else if (normHeader.includes('fim intervalo')) initialMapping[header] = 'friday_break_end';
             else if (normHeader.includes('entrada') || normHeader.includes('inicio')) initialMapping[header] = 'friday_start';
             else if (normHeader.includes('saida') || normHeader.includes('fim')) initialMapping[header] = 'friday_end';
          }
          else if (normHeader.includes('sabado')) {
             if (normHeader.includes('inicio intervalo')) initialMapping[header] = 'saturday_break_start';
             else if (normHeader.includes('fim intervalo')) initialMapping[header] = 'saturday_break_end';
             else if (normHeader.includes('entrada') || normHeader.includes('inicio')) initialMapping[header] = 'saturday_start';
             else if (normHeader.includes('saida') || normHeader.includes('fim')) initialMapping[header] = 'saturday_end';
          }
          else if (normHeader.includes('domingo')) {
             if (normHeader.includes('inicio intervalo')) initialMapping[header] = 'sunday_break_start';
             else if (normHeader.includes('fim intervalo')) initialMapping[header] = 'sunday_break_end';
             else if (normHeader.includes('entrada') || normHeader.includes('inicio')) initialMapping[header] = 'sunday_start';
             else if (normHeader.includes('saida') || normHeader.includes('fim')) initialMapping[header] = 'sunday_end';
          }
          // Generic fallback
          else if (normHeader.includes('inicio intervalo')) initialMapping[header] = 'break_start_time';
          else if (normHeader.includes('fim intervalo')) initialMapping[header] = 'break_end_time';
          else if (normHeader.includes('duracao intervalo')) initialMapping[header] = 'break_duration';
          else if (normHeader.includes('entrada') || normHeader.includes('inicio')) initialMapping[header] = 'start_time';
          else if (normHeader.includes('saida') || normHeader.includes('fim')) initialMapping[header] = 'end_time';
        });
        
        setMapping(initialMapping);
        setStep('map');
      } catch (error) {
        console.error(error);
        toast.error('Erro ao ler o arquivo.');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMappingChange = (header, dbField) => {
    setMapping(prev => ({ ...prev, [header]: dbField }));
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      
      // Transform data based on mapping
      const itemsRaw = fileData.map(row => {
        const item = { work_days: {} };
        let rowCompanyId = selectedCompanyId;
        let hasWorkDays = false;
        
        headers.forEach((header, index) => {
          const dbField = mapping[header];
          if (dbField) {
            let value = row[index];
            if (value !== undefined && value !== null) {
                // Ensure strings
                value = String(value).trim();
            }

            // Check if it's a specific day field
            const dayMatch = dbField.match(/^(monday|tuesday|wednesday|thursday|friday|saturday|sunday)_(start|end|break_start|break_end)$/);
            
            if (dayMatch) {
                const [_, day, type] = dayMatch;
                if (!item.work_days[day]) {
                    item.work_days[day] = { enabled: false };
                }
                
                // Map db field suffix to work_days key
                let key = type; // start, end
                if (type === 'break_start') key = 'breakStart';
                if (type === 'break_end') key = 'breakEnd';
                
                if (value) {
                    item.work_days[day][key] = value;
                    item.work_days[day].enabled = true;
                    hasWorkDays = true;
                }
            } else if (dbField === 'company_ref') {
                const matchedCompany = findCompany(companies, value);
                if (matchedCompany) rowCompanyId = matchedCompany.id;
                else if (!isNaN(Number(value))) rowCompanyId = Number(value);
            } else {
                item[dbField] = value;
            }
          }
        });

        if (!hasWorkDays) {
            delete item.work_days;
        }

        item.company_id = rowCompanyId;
        return item;
      }).filter(i => i.name); // Ensure name exists

      if (itemsRaw.length === 0) {
        toast.warning('Nenhum dado válido para importar. Certifique-se de mapear a coluna "Nome da Jornada".');
        setLoading(false);
        return;
      }

      const byCompany = itemsRaw.reduce((acc, item) => {
        if (!acc[item.company_id]) acc[item.company_id] = [];
        acc[item.company_id].push(item);
        return acc;
      }, {});

      let total = 0;
      for (const cId of Object.keys(byCompany)) {
        const created = await Api.importJourneys(cId, byCompany[cId]);
        if (Array.isArray(created)) total += created.length;
        else total += byCompany[cId].length;
      }
      
      toast.success(`${total} jornadas processadas com sucesso!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao importar dados. Verifique se o formato está correto.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setFileData([]);
    setHeaders([]);
    setMapping({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { reset(); onClose(); }}
      title="Importar Jornadas"
      size="lg"
    >
      <div className="import-modal-body">
        <div className="import-content-area">
            {step === 'upload' && (
                <div className="upload-step-container">
                    {companies.length > 0 && (
                       <div className="company-select-container" style={{ marginBottom: '1rem' }}>
                           <Select
                               label="Selecione a empresa padrão para importação"
                               options={[{ value: '', label: 'SELECIONE...' }, ...companies.map(c => ({ value: c.id, label: c.corporate_name || c.trade_name }))]}
                               value={selectedCompanyId}
                               onChange={e => setSelectedCompanyId(e.target.value)}
                               searchable
                           />
                       </div>
                    )}
                <div 
                    className="upload-area"
                    onClick={() => {
                        fileInputRef.current.click();
                    }}
                >
                    <FiUploadCloud className="upload-icon" />
                    <p className="upload-text">
                        Clique para selecionar um arquivo Excel (.xlsx) ou CSV
                    </p>
                    <>
                        <p className="upload-subtext">O arquivo deve conter cabeçalhos na primeira linha.</p>
                        <p className="upload-subtext">Exemplo: Empresa | Nome | Tipo | Entrada | Saída</p>
                    </>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".xlsx, .xls, .csv"
                        style={{ display: 'none' }}
                    />
                </div>
                </div>
            )}

            {step === 'map' && (
                <div className="map-step">
                    <p className="step-description">Mapeie as colunas do seu arquivo para os campos do sistema.</p>
                    
                    <div className="mapping-grid">
                        {headers.map((header, idx) => (
                            <div key={idx} className="mapping-row">
                                <div className="file-header">
                                    <span className="label">Coluna do Arquivo:</span>
                                    <strong>{header}</strong>
                                    {fileData[0] && (
                                        <span className="example-value">Ex: {fileData[0][idx]}</span>
                                    )}
                                </div>
                                <div className="arrow-icon">
                                    <FiArrowRight />
                                </div>
                                <div className="db-field-select">
                                    <Select 
                                        name={`map-${idx}`}
                                        value={mapping[header] || ''}
                                        onChange={(e) => handleMappingChange(header, e.target.value)}
                                        options={[
                                            { value: '', label: 'Ignorar' },
                                            ...DB_FIELDS
                                        ]}
                                        placeholder="Selecione o campo..."
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        <div className="modal-actions">
          <Button onClick={() => step === 'upload' ? onClose() : reset()} className="cancel-button" disabled={loading}>
            {step === 'upload' ? 'Cancelar' : 'Voltar'}
          </Button>
          
          {step === 'map' && (
            <Button onClick={handleImport} disabled={loading}>
              {loading ? 'Importando...' : 'Importar Dados'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
